import { Badge, Body1, Body1Strong, Card, Subtitle2, Title2, tokens } from '@fluentui/react-components';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Avatar, useUsers } from '@modules/users';

import { REVIEW_TYPES } from '../../constants';
import { formatReviewDate, getBasisLabel, getFindingSummary, getReviewCompletedAt } from '../../helpers';
import { TReview, TReviewBasis } from '../../types';
import { ReviewLatestUpdates } from './ReviewLatestUpdates';

type TProps = { review: TReview; bases: TReviewBasis[]; productId?: string };

export const ReviewSummaryCards = ({ review, bases, productId }: TProps) => {
  const { users, userMap } = useUsers();
  const summary = getFindingSummary(review.findings);
  const completed = summary.resolved + summary.dismissed;
  const percentage = summary.total ? Math.round((completed / summary.total) * 100) : 0;
  const participants = [
    { id: review.startedById, name: review.startedBy },
    ...review.findings.flatMap((finding) => [
      { id: finding.assigneeId, name: finding.assignee },
      ...(finding.comments ?? []).map((comment) => ({ id: comment.authorId, name: comment.author })),
    ]),
    ...(review.statusUpdates ?? []).map((update) => ({ id: update.authorId, name: update.author })),
  ];
  const reviewUsers = [
    ...new Map(
      participants
        .filter((participant) => participant.id || participant.name)
        .map((participant) => {
          const user = participant.id
            ? userMap?.[participant.id]
            : users?.find((user) => user.name === participant.name || user.mail === participant.name);
          return [
            user?._id || participant.id || participant.name,
            { user, name: user?.name || user?.mail || participant.name || 'Unknown user' },
          ] as const;
        })
    ).entries(),
  ];
  const basisLabels = review.reviewBasisIds.length
    ? review.reviewBasisIds.map((id) => getBasisLabel(bases, id))
    : ['Custom review'];

  const progress = [
    { name: 'Resolved', value: summary.resolved, color: tokens.colorBrandForeground1 },
    { name: 'Not applicable', value: summary.dismissed, color: tokens.colorBrandBackgroundInvertedSelected },
    { name: 'Open', value: summary.open, color: tokens.colorNeutralBackground4 },
  ];
  return (
    <div className="grid grid-cols-1 min-[1150px]:grid-cols-3 gap-6">
      <Card size="large" className="min-w-0 min-h-[310px]">
        <Subtitle2>Review details</Subtitle2>
        <div className="flex flex-col gap-1">
          <Body1>Users:</Body1>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {reviewUsers.map(([key, { user, name }]) => (
              <div key={key} className="flex items-center gap-2 min-w-0 max-w-full">
                <Avatar user={user} name={name} size={28} tooltip={user ? 'name' : 'none'} className="shrink-0" />
                <Body1Strong className="min-w-0 break-words">{name}</Body1Strong>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Body1>Review basis:</Body1>
          <div className="flex flex-wrap gap-2">
            {basisLabels.map((label, index) => (
              <Badge
                key={review.reviewBasisIds[index] || 'custom-review'}
                color="informative"
                appearance="tint"
                size="extra-large"
                className="!w-auto max-w-full !min-w-0 !whitespace-normal !h-auto min-h-8 !py-1"
              >
                {label}
              </Badge>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Body1>Type:</Body1>
            <Body1Strong>{REVIEW_TYPES.find((type) => type.id === review.reviewType)?.label}</Body1Strong>
          </div>
          <div className="flex flex-col gap-1">
            <Body1>Reviewed on:</Body1>
            <Body1Strong>{formatReviewDate(getReviewCompletedAt(review))}</Body1Strong>
          </div>
        </div>
      </Card>

      <Card size="large" className="min-w-0 min-h-[310px]">
        <Subtitle2>Progress</Subtitle2>
        <div
          className="relative h-[175px] shrink-0"
          role="img"
          aria-label={`${percentage}% complete, ${summary.resolved} resolved, ${summary.dismissed} not applicable, ${summary.open} open`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={
                  summary.total ? progress : [{ name: 'No findings', value: 1, color: tokens.colorNeutralBackground4 }]
                }
                dataKey="value"
                innerRadius={58}
                outerRadius={78}
                paddingAngle={progress.filter((segment) => segment.value > 0).length > 1 ? 3 : 0}
                stroke="none"
                isAnimationActive={false}
              >
                {(summary.total ? progress : [{ color: tokens.colorNeutralBackground4 }]).map((segment, index) => (
                  <Cell key={index} fill={segment.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <Title2>{percentage}%</Title2>
            <Body1 className="text-neutralFg-3">Complete</Body1>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
          {progress.map((segment) => (
            <div key={segment.name} className="flex items-center gap-2">
              <svg width="8" height="8" aria-hidden="true">
                <circle cx="4" cy="4" r="4" fill={segment.color} />
              </svg>
              <Body1>
                {segment.value} {segment.name.toLowerCase()}
              </Body1>
            </div>
          ))}
        </div>
        <Body1 className="w-full text-center text-neutralFg-3 mt-auto">
          {completed} of {summary.total} findings completed
        </Body1>
      </Card>

      <ReviewLatestUpdates review={review} productId={productId} />
    </div>
  );
};
